import { memo, useEffect, useState } from 'react';
import { MdOutlineWifiOff } from 'react-icons/md';

const UPDATE_INTERVAL_MS = 60_000;

type LastKnownLocationDisplayProps = {
  timestamp: string;
};

const formatElapsedTime = (timestamp: string): string | null => {
  const timestampMs = Date.parse(timestamp);

  if (Number.isNaN(timestampMs)) {
    return null;
  }

  const elapsedMs = Math.max(0, Date.now() - timestampMs);
  const totalMinutes = Math.floor(elapsedMs / 60_000);

  if (totalMinutes < 60) {
    return `${totalMinutes}m`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (totalHours < 24) {
    return remainingMinutes > 0 ? `${totalHours}h ${remainingMinutes}m` : `${totalHours}h`;
  }

  const days = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`;
};

const LastKnownLocationDisplayComponent = ({ timestamp }: LastKnownLocationDisplayProps) => {
  const [elapsedTime, setElapsedTime] = useState<string | null>(() => formatElapsedTime(timestamp));

  useEffect(() => {
    const updateElapsedTime = () => {
      setElapsedTime(formatElapsedTime(timestamp));
    };

    updateElapsedTime();

    const intervalId = window.setInterval(updateElapsedTime, UPDATE_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [timestamp]);

  if (!elapsedTime) {
    return null;
  }

  return (
    <div
      className="mt-2 flex w-fit items-center gap-2 self-center rounded-full bg-base-90 px-4 py-2"
      role="status"
      aria-live="polite"
      aria-label={`Last updated ${elapsedTime} ago`}
    >
      <MdOutlineWifiOff className="h-5 w-5 shrink-0 text-base-25" aria-hidden="true" />

      <span className="whitespace-nowrap text-s-r text-base-5">Last updated {elapsedTime} ago</span>
    </div>
  );
};

export const LastKnownLocationDisplay = memo(LastKnownLocationDisplayComponent);
